(ns practitest.frontend.subs
  (:require
    [re-frame.core :as re-frame]
    [clojure.data :refer [diff]]
    [clojure.set :refer [rename-keys]]
    [practitest.frontend.common.i18n :refer [get-translation]]))

(def index-grid-keys {:requirement :requirements-grid
                      :test        :tests-grid
                      :testset     :testsets-grid
                      :issue       :issues-grid
                      :testsets-instances :testsets-instances-grid})

(re-frame/reg-sub
  ::options
  (fn [db _]
    (:options db)))

(re-frame/reg-sub
 ::system-messages
 :<- [::options]
 (fn [{:keys [system-messages]} _]
   system-messages))

(re-frame/reg-sub
  ::status-colors
  (fn [_]
    (re-frame/subscribe [::options]))
  (fn [{:keys [status-colors]} _]
    (into {} status-colors)))

(re-frame/reg-sub
  ::entity-fields
  (fn [db [_ entity]]
    (get-in db [:entity-fields entity])))

(re-frame/reg-sub
  ::entity-sections
  (fn [db [_ entity]]
    (get-in db [:entity-sections entity])))

(re-frame/reg-sub
  ::online-users
  (fn [db _]
    (:online-users db)))

(re-frame/reg-sub
 ::all-entity-sections
 (fn [db [_]]
   (:entity-sections db)))

(re-frame/reg-sub
  ::all-entity-fields
  (fn [db _]
    (:entity-fields db)))

(def entity-types [:test :issue :requirement :testset :instance :run])

(def ^:private system-fields-by-entity
  "Fields stored in :fields but not assigned to any entity via :entity-fields."
  {:test #{:name}
   :testset #{:name}
   :issue #{:title}
   :requirement #{:name}
   ;; When new run form FF is on, backend drops these fields from Run entity
   ;; But PTQL supports them
   :run #{:run_run_status :run_type}})

(def ^:private synthetic-fields-by-entity
  "Fields not in :fields at all but supported by PTQL - inject as synthetic descriptors."
  {:test [{:id "display_id" :name "ID" :type "number"}
          {:id "linked_requirements" :name "Linked Requirements" :type "linked"}
          {:id "linked_issues" :name "Linked Issues" :type "linked"}
          {:id "linked_testsets" :name "Linked Testsets" :type "linked"}]
   :testset [{:id "display_id" :name "ID" :type "number"}]
   :issue [{:id "display_id" :name "ID" :type "text"}
           {:id "external_id" :name "External ID" :type "text"}
           {:id "linked_tests" :name "Linked Tests" :type "linked"}
           {:id "linked_issues" :name "Linked Issues" :type "linked"}
           {:id "linked_requirements" :name "Linked Requirements" :type "linked"}]
   :requirement [{:id "display_id" :name "ID" :type "text"}
                 {:id "external_id" :name "External ID" :type "text"}
                 {:id "linked_tests" :name "Linked Tests / Test sets" :type "linked"}
                 {:id "linked_issues" :name "Linked Issues" :type "linked"}]
   :run [{:id "tester" :name "Tester" :type "user"}
         {:id "run_date" :name "Run Date" :type "date"}]})

;; TODO: should we allow filtering by these fields?
;;       it is potentially heavy, but it can be very useful
(def ^:private computed-duration-fields
  "Criteria on these are backend no-ops (PT1-8562), so hide them from filter field lists."
  #{:duration_estimate :duration_average :estimated_duration :actual_duration})

(def entity-kw->ptql-name
  {:test "Test"
   :testset "TestSet"
   :issue "Issue"
   :requirement "Requirement"
   :instance "Instance"
   :run "Run"})

(defn- field-descriptor [fields-meta field-key]
  (when-let [field-meta (get fields-meta field-key)]
    {:id (if (= :tags field-key) "__TAG__" (name field-key))
     :name (or (:label field-meta) (name field-key))
     :type (some-> (:type field-meta) (name))
     :values (when (and (seq (:possible-values field-meta))
                        (not= "user" (:type field-meta)))
               (vec (:possible-values field-meta)))}))

(defn- entity-field-keys [entity-fields-by-kw entity]
  (->> (concat (keys (get entity-fields-by-kw entity))
               (get system-fields-by-entity entity #{}))
       (distinct)
       (remove computed-duration-fields)))

(defn entity-fields-grouped
  "Returns {entity-kw [{:name :type :values}, ...], ...} for the PTQL entity types: roots and LINKED TO targets.
  Combines :entity-fields with :fields metadata, adds system fields (name/title)
  and synthetic ID descriptors."
  [fields-meta entity-fields-by-kw]
  (into {}
        (map (fn [entity]
               [entity
                (vec (concat (keep #(field-descriptor fields-meta %)
                                   (entity-field-keys entity-fields-by-kw entity))
                             (get synthetic-fields-by-entity entity [])))]))
        entity-types))

;; TODO: when we get to refactoring taskboard (aka kanban), we will move it there
(def ^:private task-board-entities #{:test :testset :issue :requirement})

(defn- add-task-board-lane [fields entity lanes]
  (cond-> fields
    (and (seq lanes) (task-board-entities entity))
    (conj {:id "task_board_lane"
           ;; NOTE: not translated, the builder writes this name into PTQL and resolver only knows this name.
           :name "Task Board Lane"
           :type "task_board_lane"
           :values (mapv (juxt :id :name) lanes)})))

(defn fields-by-ptql-entity
  ([all-fields entity-fields] (fields-by-ptql-entity all-fields entity-fields nil))
  ([all-fields entity-fields lanes]
   (->> (entity-fields-grouped all-fields entity-fields)
        (reduce-kv (fn [m k v]
                     (if-let [ptql-name (entity-kw->ptql-name k)]
                       (assoc m ptql-name (add-task-board-lane v k lanes))
                       m))
                   {}))))

(re-frame/reg-sub
  ::fields-by-ptql-entity
  :<- [::fields]
  :<- [::all-entity-fields]
  :<- [:practitest.frontend.kanban/lanes]
  (fn [[fields entity-fields-by-kw lanes] _]
    (fields-by-ptql-entity fields entity-fields-by-kw lanes)))

(re-frame/reg-sub
  ::integrated-fields
  (fn [db _]
    (:integrated-fields db)))

(re-frame/reg-sub
  ::fields
  (fn [db _]
    (:fields db)))

(re-frame/reg-sub
  ::settings-fields
  (fn [db _]
    (:settings-fields db)))

(re-frame/reg-sub
  ::sections-deletion-prevention-data
  (fn [db _]
    (get-in db [:sections :deletion-prevention-data])))

(re-frame/reg-sub
  ::users
  (fn [db _]
    (:users db)))

;; Users referenced by entity payloads but not in the project's :users list
;; (typically users removed from the project/account). Keyed by id. Populated
;; by handlers that merge :referenced_users from server responses. Used by
;; find-user as a fallback so attribution still renders.
(re-frame/reg-sub
  ::missing-users
  (fn [db _]
    (:missing-users db)))

(re-frame/reg-sub
  ::groups
  (fn [db _]
    (:groups db)))

(defn get-current-user
  [db]
  (:current-user db))

(re-frame/reg-sub
  ::current-user
  (fn [db _]
    (get-current-user db)))

(re-frame/reg-sub
  ::current-user-timezone
  (fn [db _]
    (get-in db [:current-user :time_zone])))

;; TODO: https://github.com/den1k/re-frame-utils/blob/master/src/vimsical/re_frame/cofx/inject.cljc ??
(defn get-current-project-id
  [db]
  (get-in db [:current-project :id]))

(defn get-current-account-id
  [db]
  (get-in db [:current-account :id]))

(defn get-current-integration-id
  [db]
  (get-in db [:current-project :integrated_bugtracker_id]))

(re-frame/reg-sub
 ::current-project-id
 (fn [db _]
   (get-current-project-id db)))

(defn get-current-project
  [db]
  (:current-project db))

(re-frame/reg-sub
  ::current-project
  (fn [db _]
    (get-current-project db)))

(re-frame/reg-sub
  ::enabled-automation-types
  (fn [db _]
    (set (get-in db [:current-project :automation_types]))))

(re-frame/reg-sub
  ::user-projects
  (fn [db]
    (:user-projects db)))

(re-frame/reg-sub
  ::user-accounts
  (fn [db]
    (:user-accounts db)))

(re-frame/reg-sub
  ::current-account
  (fn [db]
    (:current-account db)))

(re-frame/reg-sub
  ::account-license
  (fn [db _]
    (get-in db [:current-account :license])))

(re-frame/reg-sub
  ::pt-datacenter-account
  (fn [db _]
      (get-in db [:options :pt-datacenter])))

(re-frame/reg-sub
  ::tags
  (fn [db _]
    (:tags db)))

(re-frame/reg-sub
  ::entity
  (fn [db [_ entity]]
    (get-in db [:entity entity])))

(def label-prefix-defaults
  {:run     "Run"
   :lastrun "Last Run"
   :test    "Test"
   :testset "Test Set"})

(def label-prefix-translation-key
  {:run     "run.singular"
   :lastrun "requirement.coverage.last_run"
   :test    "test.singular"
   :testset "testset.singular"})

(defn namespaced-label [entity label]
  (str (get-translation (get label-prefix-translation-key entity) (get label-prefix-defaults entity))
       ": "
       label))

(defn parse-namespaced-field [namespaced-field]
  [(-> namespaced-field namespace keyword)
   (-> namespaced-field name keyword)])

(defn get-form-state
  [db form-id]
  (get-in db [:form-states form-id]))

(re-frame/reg-sub
  ::form-state
  (fn [db [_ form-id]]
    (get-form-state db form-id)))

(defn get-field-state
  [form-state field-id]
  (get-in form-state [:fields field-id]))

(re-frame/reg-sub
  ::field-state
  (fn [[_ form-id _]]
    (re-frame/subscribe [::form-state form-id]))
  (fn [form-state [_ _ field-id]]
    (get-field-state form-state field-id)))

;; TODO: not really scalable
(defn get-current-entity-type
  [db]
  (:current-entity db))

(defn get-current-entity-id
  [db entity-type]
  (get-in db [:entity entity-type :current :id]))

(re-frame/reg-sub
  ::current-entity-id
  (fn [db [_ entity]]
    (get-current-entity-id db entity)))

;; TODO: fix this
(re-frame/reg-event-db
  ::set-current-entity-id
  (fn [db [_ entity id]]
    (assoc-in db [:entity entity :current :id] id)))

(re-frame/reg-sub
  ::current-entity-key
  (fn [db [_]]
    (keyword (get-in db [:options :entity-key]))))

(re-frame/reg-sub
  ::current-entity
  (fn [db [_]]
    (:current-entity db)))

(re-frame/reg-sub
  ::progress?
  (fn [db [_ event]]
    (contains? (:progress db) event)))

(re-frame/reg-sub
 ::collab-connected?
 (fn [db _]
   (= :connected (get-in db [:collab :state]))))

(re-frame/reg-sub
 ::remote-changes
 (fn [[_ entity]]
   [(re-frame/subscribe [::entity entity])
    (re-frame/subscribe [::fields])])
 (fn [[{:keys [remote current state changed-by]} fields] _]
   (when-not (empty? remote)
     (let [blacklist [:updated_at]
           [a b _]   (diff (apply dissoc remote blacklist)
                           (apply dissoc current blacklist))
           mapping   (->> fields
                          (map (fn [[k {:keys [label]}]]
                                 [k label]))
                          (into {}))]
       (when (or (not-empty a)
                 (not-empty b))
         {:remote {:raw        a
                   :translated (rename-keys a mapping)}
          :local  {:raw        b
                   :translated (rename-keys b mapping)}
          :changed-by changed-by})))))

(re-frame/reg-sub
 ::current-locale
 :<- [::options]
 (fn [{:keys [current-locale]} _]
   current-locale))

;; TODO: ::field subscription ?
(defn get-field-by-id [id]
  (let [fields @(re-frame/subscribe [::fields])]
    (get fields (keyword id))))

(re-frame/reg-sub
  ::use-test-workflow?
  :<- [::options]
  (fn [{:keys [use-test-workflows?]} _]
      (boolean use-test-workflows?)))

(re-frame/reg-sub
  ::test-status-default-workflow?
  :<- [::options]
  (fn [{:keys [test-status-default-workflow?]} _]
      (boolean test-status-default-workflow?)))

(re-frame/reg-sub
  ::generative-ai-consent?
  :<- [::options]
  (fn [{:keys [generative-ai-consent?]}]
    (boolean generative-ai-consent?)))

(re-frame/reg-sub
  ::project-assistant-enabled?
  :<- [::options]
  (fn [{:keys [project-assistant-enabled?]}]
    (boolean project-assistant-enabled?)))

(re-frame/reg-sub
  ::usage-and-limits-enabled?
  :<- [::options]
  (fn [{:keys [usage-and-limits-enabled?]}]
    (boolean usage-and-limits-enabled?)))

(re-frame/reg-sub
  ::new-users-view-enabled?
  :<- [::options]
  (fn [{:keys [new-users-view-enabled?]}]
    (boolean new-users-view-enabled?)))

;; Backend CommonBatchController::BATCH_LIMIT, delivered via frontend_config.
;; Falls back to 2000 only if the config key is absent.
(re-frame/reg-sub
  ::batch-limit
  :<- [::options]
  (fn [{:keys [batch-limit]}]
    (or batch-limit 2000)))

(re-frame/reg-sub
  ::new-view-editor-enabled?
  :<- [::options]
  (fn [{:keys [new-view-editor-enabled?]}]
    (boolean new-view-editor-enabled?)))

(re-frame/reg-sub
 ::ptql-debug-bar-enabled?
 :<- [::options]
 (fn [{:keys [ptql-debug-bar-enabled?]}]
   (boolean ptql-debug-bar-enabled?)))

(re-frame/reg-sub
 ::kanban-enabled?
 :<- [::options]
 (fn [{:keys [kanban-enabled?]}]
   (boolean kanban-enabled?)))

(re-frame/reg-sub
  ::test-cloning-update-enabled?
  :<- [::options]
  (fn [{:keys [test-cloning-update-enabled?]}]
    (boolean test-cloning-update-enabled?)))

(re-frame/reg-sub
  ::edit-global-field-values?
  :<- [::options]
  (fn [{:keys [edit-global-field-values?]}]
    (boolean edit-global-field-values?)))

(re-frame/reg-sub
  ::convert-global-field-type-enabled?
  :<- [::options]
  (fn [{:keys [convert-global-field-type-enabled?]}]
    (boolean convert-global-field-type-enabled?)))

(re-frame/reg-sub
  ::new-run-form-enabled?
  :<- [::options]
  (fn [{:keys [new-run-form?]}]
    (boolean new-run-form?)))

(re-frame/reg-sub
  ::new-batch-edit-modal?
  :<- [::options]
  (fn [{:keys [new-batch-edit-modal?]}]
    (boolean new-batch-edit-modal?)))

(re-frame/reg-sub
 ::get-field-in-form-state2
(fn [db [_ form-id field]]
  (get-in db [:form-states2 form-id :fields field])))

(re-frame/reg-sub
 ::global-fields-supported?
 :<- [::options]
 (fn [{:keys [global-fields-supported?]}]
   (boolean global-fields-supported?)))

(re-frame/reg-sub
  ::test-case-versioning-enabled?
  :<- [::options]
  (fn [{:keys [test-case-versioning-enabled?]}]
    (boolean test-case-versioning-enabled?)))

(re-frame/reg-sub
  ::versioning-can-use-labels?
  :<- [::options]
  (fn [{:keys [versioning-can-use-labels?]}]
    (boolean versioning-can-use-labels?)))

(re-frame/reg-sub
  ::versioning-can-create-labels?
  :<- [::options]
  (fn [{:keys [versioning-can-create-labels?]}]
    (boolean versioning-can-create-labels?)))

(re-frame/reg-sub
  ::history-filter
  :<- [:practitest.frontend.common-entities.events/history-filter]
  (fn [filter _]
    filter))

(def readiness-index-allowed-to #{"Corporate" "Team"})

(defn milestone-readiness-index-enabled? [db]
  (boolean (readiness-index-allowed-to (get-in db [:current-account :license]))))

(re-frame/reg-sub
 ::milestone-sprint-sync-enabled?
 :<- [::options]
 (fn [{:keys [milestone-sprint-sync-enabled?]}]
   (boolean milestone-sprint-sync-enabled?)))

(re-frame/reg-sub
 ::milestone-readiness-index-enabled?
 (fn [db _]
   (milestone-readiness-index-enabled? db)))
